
import PaginationResponseT from "@/api/dataSource/api/Common/ResponseModels/PaginationResponseT";
import FilterRequest from "@/api/dataSource/api/Spirit/Common/Extensions/Models/FilterRequest";
import {
  GetUserGoalSurveyResponse,
  UserGoalSurveyItem,
} from "@/api/dataSource/api/Spirit/Admin/Services/UserService";
import UsersGetListResponse from "@/api/dataSource/api/Spirit/Admin/Interfaces/Models/UserService/UsersGetListResponse";
import UserFilterModel from "@models/users/userFilterModel";
import Vue from "vue";
import { Component } from "vue-property-decorator";

@Component
export default class PageUsersComponent extends Vue {
  readonly expiredUsersFetchLimit = 10000;
  filter: UserFilterModel = new UserFilterModel();
  tableKey = 0;
  cachedUsers: UsersGetListResponse[] = [];
  subscriptionDays: Record<number, string | number> = {};
  isSurveyModalVisible = false;
  isSurveyLoading = false;
  surveyLoadError = "";
  surveyItems: UserGoalSurveyItem[] = [];

  columnNames = [
    "#",
    "Имя",
    "Email",
    "Статус",
    "Окончание",
    "Минуты",
    "Дней подряд",
    "Создано",
    "Дней",
    "Премиум",
    "Анкета",
    "",
  ];

  subscriptionStatusOptions = [
    { id: "Free", name: "Бесплатная" },
    { id: "Active", name: "Активная" },
    { id: "Expired", name: "Истекла" },
  ];

  getDataFunc = this.getUsers.bind(this);

  getUserId(user: UsersGetListResponse): number {
    return Number(
      (user as any).Id ||
        (user as any).id ||
        (user as any).UserId ||
        (user as any).userId ||
        0
    );
  }

  getUserName(user: UsersGetListResponse): string {
    return (
      (
        this.readValue(user, "Name", "name") ||
        this.readValue(user, "FullName", "fullName") ||
        this.readValue(user, "UserName", "userName") ||
        ""
      ).trim() ||
      "Без имени"
    );
  }

  getUserEmail(user: UsersGetListResponse): string {
    return (
      this.readValue(user, "Email", "email") ||
      this.readValue(user, "Login", "login") ||
      "-"
    );
  }

  getSubscriptionStatusText(user: UsersGetListResponse): string {
    const status = this.getSubscriptionStatusValue(user);

    switch (status) {
      case "Free":
        return "Бесплатная";
      case "Active":
        return "Активная";
      case "Expired":
        return "Истекла";
      default:
        return "-";
    }
  }

  getSubscriptionTypeText(user: UsersGetListResponse): string {
    const type = this.readValue(user, "SubscriptionType", "subscriptionType");

    switch (type) {
      case "IOS":
      case 0:
        return "iOS";
      case "Android":
      case 1:
        return "Android";
      case "Cloud":
      case 2:
        return "Cloud";
      default:
        return "-";
    }
  }

  getPremiumDate(user: UsersGetListResponse): Date | string | null {
    return (
      this.readValue(user, "PremiumExpirationDate", "premiumExpirationDate") ||
      this.readValue(user, "EndExpirationDate", "endExpirationDate") ||
      this.readValue(user, "PremiumEndDate", "premiumEndDate") ||
      this.readValue(user, "SubscriptionEndDate", "subscriptionEndDate") ||
      this.readValue(user, "PremiumExpireDate", "premiumExpireDate") ||
      this.readValue(user, "PremiumTo", "premiumTo") ||
      this.readValue(user, "SubscriptionTo", "subscriptionTo") ||
      null
    );
  }

  getCreatedDate(user: UsersGetListResponse): Date | string | null {
    return (
      this.readValue(user, "RegistrationDate", "registrationDate") ||
      this.readValue(user, "DateCreated", "dateCreated") ||
      this.readValue(user, "Created", "created") ||
      this.readValue(user, "CreatedAt", "createdAt") ||
      null
    );
  }

  getTotalListenMinutes(user: UsersGetListResponse): number {
    return Number(
      this.readValue(user, "TotalListenMinutes", "totalListenMinutes") ||
        this.readValue(user, "TotalListeningTime", "totalListeningTime") ||
        0
    );
  }

  getCurrentStreakDays(user: UsersGetListResponse): number {
    return Number(
      this.readValue(user, "CurrentStreakDays", "currentStreakDays") ||
        this.readValue(user, "CurrentSeries", "currentSeries") ||
        0
    );
  }

  getLevelName(user: UsersGetListResponse): string {
    return this.readValue(user, "LevelName", "levelName") || "-";
  }

  readValue(source: any, pascalKey: string, camelKey: string): any {
    if (!source) {
      return null;
    }

    if (source[pascalKey] !== undefined && source[pascalKey] !== null) {
      return source[pascalKey];
    }

    if (source[camelKey] !== undefined && source[camelKey] !== null) {
      return source[camelKey];
    }

    return null;
  }

  getDateText(value: Date | string | null): string {
    if (!value) {
      return "-";
    }

    const date = this.$options.filters.moment(value, "DD.MM.YYYY");
    return date || "-";
  }

  async getUsers(
    request: FilterRequest = null
  ): Promise<PaginationResponseT<UsersGetListResponse>> {
    const requestedStatus = this.getRequestedSubscriptionStatus(request);
    const isClientSideExpiredFilter = requestedStatus === "Expired";
    const apiRequest =
      isClientSideExpiredFilter
        ? this.getExpiredUsersRequest(request)
        : request;
    const response = await this.$api.UserService.getListAsync(apiRequest);
    const responseItems = this.readResponseItems(response);
    this.cachedUsers = requestedStatus
      ? responseItems.filter((user) => this.getSubscriptionStatusValue(user) === requestedStatus)
      : responseItems;

    const pagination = request && (request as any).Pagination;
    const skip = pagination && pagination.Skip ? pagination.Skip : 0;
    const take = pagination && pagination.Take ? pagination.Take : this.cachedUsers.length;
    const totalCount =
      requestedStatus && this.cachedUsers.length !== responseItems.length
        ? this.cachedUsers.length
        : this.readResponseCount(response, this.cachedUsers.length);

    return new PaginationResponseT<UsersGetListResponse>({
      Count: totalCount,
      Items: isClientSideExpiredFilter || Array.isArray(response)
        ? this.cachedUsers.slice(skip, skip + take)
        : this.cachedUsers,
    });
  }

  getExpiredUsersRequest(request: any): any {
    return {
      ...(request || {}),
      Page: {
        Skip: 0,
        Take: this.expiredUsersFetchLimit,
      },
      Pagination: {
        Skip: 0,
        Take: this.expiredUsersFetchLimit,
      },
      SubscriptionStatus: null,
    };
  }

  readResponseItems(response: any): UsersGetListResponse[] {
    if (Array.isArray(response)) {
      return response;
    }

    const items = this.readValue(response, "Items", "items");
    return Array.isArray(items) ? items : [];
  }

  getRequestedSubscriptionStatus(request: any): string | null {
    const status = request && (request.SubscriptionStatus || request.subscriptionStatus);
    return this.normalizeSubscriptionStatus(status);
  }

  getSubscriptionStatusValue(user: UsersGetListResponse): string | null {
    const premiumDate = this.toValidDate(this.getPremiumDate(user));

    if (premiumDate) {
      return premiumDate.getTime() < Date.now() ? "Expired" : "Active";
    }

    return this.normalizeSubscriptionStatus(
      this.readValue(user, "SubscriptionStatus", "subscriptionStatus")
    );
  }

  normalizeSubscriptionStatus(status: any): string | null {
    if (status && typeof status === "object") {
      return this.normalizeSubscriptionStatus(
        status.id || status.Id || status.value || status.Value
      );
    }

    if (typeof status === "string") {
      const normalizedStatus = status.trim().toLowerCase();
      switch (normalizedStatus) {
        case "free":
        case "0":
          return "Free";
        case "active":
        case "1":
          return "Active";
        case "expired":
        case "2":
          return "Expired";
        default:
          return null;
      }
    }

    switch (status) {
      case 0:
        return "Free";
      case 1:
        return "Active";
      case 2:
        return "Expired";
      default:
        return null;
    }
  }

  readResponseCount(response: any, fallback: number): number {
    const page = this.readValue(response, "Page", "page");
    const count = this.readValue(page, "Count", "count");
    return count == null ? fallback : Number(count);
  }

  async updateSubscription(user: UsersGetListResponse) {
    const userId = this.getUserId(user);
    const daysValue = Number(this.subscriptionDays[userId]);

    if (!daysValue || daysValue < 1) {
      this.$notification.error("Укажите количество дней премиума");
      return;
    }

    try {
      const premiumExpirationDate = this.getRenewedPremiumDate(user, daysValue);
      const result = await this.$api.UserService.updateSubscriptionAsync({
        userId,
        premiumExpirationDate: premiumExpirationDate.toISOString(),
      });

      if (result) {
        this.setUserPremiumDate(user, premiumExpirationDate);
        this.subscriptionDays[userId] = "";
        this.$dialog.success("Подписка успешно обновлена");
        await this.refreshTable();
      }
    } catch (error) {
      this.$notification.error((error as any).ErrorMessage || error);
    }
  }

  getRenewedPremiumDate(user: UsersGetListResponse, days: number): Date {
    const currentPremiumDate = this.toValidDate(this.getPremiumDate(user));
    const now = new Date();
    const startDate =
      currentPremiumDate && currentPremiumDate.getTime() > now.getTime()
        ? currentPremiumDate
        : now;
    const renewedDate = new Date(startDate.getTime());
    renewedDate.setDate(renewedDate.getDate() + days);
    return renewedDate;
  }

  toValidDate(value: Date | string | null): Date | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  setUserPremiumDate(user: UsersGetListResponse, value: Date) {
    const isoValue = value.toISOString();
    this.$set(user as any, "PremiumExpirationDate", isoValue);
    this.$set(user as any, "premiumExpirationDate", isoValue);
  }

  changeSubscriptionDays(user: UsersGetListResponse, delta: number) {
    const userId = this.getUserId(user);
    const currentValue = Number(this.subscriptionDays[userId]) || 0;
    const nextValue = currentValue + delta;
    this.$set(this.subscriptionDays, userId, nextValue > 0 ? nextValue : "");
  }

  async startDelete(user: UsersGetListResponse) {
    const userId = this.getUserId(user);
    const confirm = await this.$dialog.confirm(
      `Вы действительно хотите удалить пользователя #${userId}?`
    );

    if (!confirm) {
      return;
    }

    try {
      const result = await this.$api.UserService.deleteAsync(userId);
      if (result) {
        this.$dialog.success("Пользователь удален");
        await this.refreshTable();
      }
    } catch (error) {
      this.$notification.error((error as any).ErrorMessage || error);
    }
  }

  async refreshTable() {
    const table = this.$refs.table as any;
    if (table && typeof table.fetchData === "function") {
      await table.fetchData();
      return;
    }

    this.tableKey += 1;
  }

  async openSurvey(user: UsersGetListResponse) {
    const userId = this.getUserId(user);
    this.isSurveyModalVisible = true;
    this.isSurveyLoading = true;
    this.surveyLoadError = "";
    this.surveyItems = [];

    try {
      const response = await this.$api.UserService.getSurveyAsync(userId);
      this.surveyItems = this.readSurveyItems(response);
    } catch (error) {
      this.surveyLoadError =
        (error as any).ErrorMessage || "Не удалось загрузить анкету пользователя";
    } finally {
      this.isSurveyLoading = false;
    }
  }

  closeSurvey() {
    this.isSurveyModalVisible = false;
    this.isSurveyLoading = false;
    this.surveyLoadError = "";
    this.surveyItems = [];
  }

  readSurveyItems(response: GetUserGoalSurveyResponse | null): UserGoalSurveyItem[] {
    const items = this.readValue(response, "Items", "items");
    return Array.isArray(items) ? items : [];
  }

  getSurveyGoalText(item: UserGoalSurveyItem): string {
    switch (this.readValue(item, "GoalType", "goalType")) {
      case "SelfEsteem":
        return "Самооценка и уверенность";
      case "Health":
        return "Здоровье и благополучие";
      case "Success":
        return "Успех и достижение целей";
      case "Love":
        return "Любовь и отношения";
      case "Finance":
        return "Финансовое благополучие";
      default:
        return "Не указано";
    }
  }

  getSurveyExperienceText(item: UserGoalSurveyItem): string {
    switch (this.readValue(item, "Experience", "experience")) {
      case "Beginner":
        return "Начинающий";
      case "Intermediate":
        return "Средний";
      case "Advanced":
        return "Опытный";
      default:
        return "Не указано";
    }
  }

  getSurveySkillText(item: UserGoalSurveyItem): string {
    switch (this.readValue(item, "Skill", "skill")) {
      case "SelfEsteem":
        return "Самооценка";
      case "Health":
        return "Здоровье";
      case "Success":
        return "Успех";
      case "Love":
        return "Любовь";
      case "Finance":
        return "Финансы";
      case "PeaceAndStress":
        return "Спокойствие и стресс";
      default:
        return "Не указано";
    }
  }

  getSurveyDayTimeText(item: UserGoalSurveyItem): string {
    switch (this.readValue(item, "DayTime", "dayTime")) {
      case "Morning":
        return "Утро";
      case "Day":
        return "День";
      case "Evening":
        return "Вечер";
      case "Night":
        return "Ночь";
      default:
        return "Не указано";
    }
  }

  getSurveyFavoriteTypeText(item: UserGoalSurveyItem): string {
    switch (this.readValue(item, "FavoriteTypes", "favoriteTypes")) {
      case "Text":
        return "Текст";
      case "Audio":
        return "Аудио";
      case "Video":
        return "Видео";
      default:
        return "Не указано";
    }
  }

  getSurveyGenderText(item: UserGoalSurveyItem): string {
    switch (this.readValue(item, "Gender", "gender")) {
      case "Male":
        return "Мужской";
      case "Female":
        return "Женский";
      default:
        return "Не указано";
    }
  }

  getSurveyDateCreated(item: UserGoalSurveyItem): Date | string | null {
    return this.readValue(item, "DateCreated", "dateCreated");
  }
}
